import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Tooltip, Typography } from "@mui/material";
import { C, MONO, cardSx, CATEGORY_INFO, categoryLabel, formatNumber, formatMs, formatTimeAgo } from "./usageTheme";
import { MethodPill, EndpointText, StatusPill } from "./UsageBits";

// Panels for the "is anything wrong?" part of the Usage dashboard — what
// failed and why, Shopify webhook delivery, the login/email funnel, and the
// slowest calls. All read from the props the Usage page already loaded.

function CardTitle({ title, subtitle, right }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 2 }}>
      <Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: 12.5, color: C.textSecondary, mt: "2px" }}>{subtitle}</Typography>}
      </Box>
      {right}
    </Box>
  );
}

function Empty({ children }) {
  return <Typography sx={{ fontSize: 13, color: C.textFaint, textAlign: "center", py: 3 }}>{children}</Typography>;
}

const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);

// What failed and why — real problems first (red), then normal-operation
// failures (muted). Clicking a row opens the matching requests.
export function IssuesCard({ byCategory = [], onSelect, sx }) {
  const real = byCategory.filter((c) => !c.expected);
  const expected = byCategory.filter((c) => c.expected);
  const max = Math.max(1, ...byCategory.map((c) => c.calls));

  const Row = ({ c }) => {
    const info = CATEGORY_INFO[c.category];
    return (
      <Box
        onClick={() => onSelect(c)}
        sx={{ cursor: "pointer", borderRadius: "10px", px: 1, py: 0.75, mx: -1, "&:hover": { background: "#FAFAFA" } }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2, mb: 0.6 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: c.expected ? C.textBody : C.error }}>
              {categoryLabel(c.category)}
            </Typography>
            {info?.hint && (
              <Typography component="span" sx={{ fontSize: 11.5, color: C.textFaint, ml: 1 }}>
                {info.hint}
              </Typography>
            )}
          </Box>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.ink, flexShrink: 0 }}>
            {formatNumber(c.calls)}
            <Box component="span" sx={{ color: C.textFaint, fontWeight: 400 }}>
              {" "}· {formatNumber(c.affected)} {c.affected === 1 ? "user/IP" : "users/IPs"}
            </Box>
          </Typography>
        </Box>
        <Box sx={{ height: 5, borderRadius: 3, background: C.gray7, overflow: "hidden" }}>
          <Box sx={{ height: "100%", width: `${(c.calls / max) * 100}%`, background: c.expected ? C.gray3 : C.error, borderRadius: 3 }} />
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ ...cardSx, p: 3, minWidth: 0, ...sx }}>
      <CardTitle title="What went wrong" subtitle="Failed calls by cause — click to see them" />
      {byCategory.length === 0 ? (
        <Empty>No failed calls in this range.</Empty>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: real.length ? C.error : C.ok, mb: 1 }}>
              {real.length ? "Needs attention" : "No real problems"}
            </Typography>
            {real.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: C.textSecondary }}>Every failure in this range is part of normal operation.</Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {real.map((c) => <Row key={c.category} c={c} />)}
              </Box>
            )}
          </Box>
          {expected.length > 0 && (
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: C.textFaint, mb: 1 }}>
                Expected — normal operation
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {expected.map((c) => <Row key={c.category} c={c} />)}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// Shopify order webhooks: delivered vs "retry later" (update arrived before
// the order existed — Shopify retries automatically) vs genuine failures.
export function WebhookCard({ webhooks }) {
  const w = webhooks || { total: 0, ok: 0, retries: 0, failed: 0 };
  const okPct = pct(w.ok, w.total);
  const segments = [
    { key: "ok", label: "Delivered", value: w.ok, color: C.ok },
    { key: "retries", label: "Retried by Shopify", value: w.retries, color: C.gray3 },
    { key: "failed", label: "Failed", value: w.failed, color: C.error },
  ];
  return (
    <Box sx={{ ...cardSx, p: 3, minWidth: 0 }}>
      <CardTitle
        title="Shopify webhooks"
        subtitle="New and updated orders from the shop"
        right={
          w.total > 0 && (
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: w.failed ? C.error : C.ink }}>
              {okPct}%
              <Box component="span" sx={{ fontSize: 11.5, fontWeight: 500, color: C.textSecondary, ml: 0.5 }}>
                delivered
              </Box>
            </Typography>
          )
        }
      />
      {w.total === 0 ? (
        <Empty>No webhooks in this range.</Empty>
      ) : (
        <>
          <Box sx={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", background: C.gray7, mb: 2 }}>
            {segments.filter((s) => s.value > 0).map((s) => (
              <Box key={s.key} sx={{ width: `${(s.value / w.total) * 100}%`, background: s.color }} />
            ))}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {segments.map((s) => (
              <Box key={s.key} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: s.color }} />
                  <Typography sx={{ fontSize: 13, color: C.textBody }}>{s.label}</Typography>
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: s.key === "failed" && s.value ? C.error : C.ink }}>
                  {formatNumber(s.value)}
                </Typography>
              </Box>
            ))}
          </Box>
          {w.retries > 0 && (
            <Typography sx={{ fontSize: 11.5, color: C.textFaint, mt: 1.5, lineHeight: 1.45 }}>
              Retries happen when Shopify sends an order update before the new order itself — Shopify re-sends it shortly after.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

const FLOW_LABELS = { login: "Login code", signup: "Registration", reset: "Password reset" };

function FunnelRow({ label, data }) {
  const steps = [
    { label: "Email sent", value: data.people },
    { label: "Code entered", value: data.code_entered },
    { label: "Completed", value: data.completed },
  ];
  const lost = data.people - data.code_entered;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: C.ink }}>{label}</Typography>
        <Typography sx={{ fontSize: 12, color: C.textSecondary }}>
          {formatNumber(data.emails_sent)} {data.emails_sent === 1 ? "email" : "emails"}
        </Typography>
      </Box>
      {data.people === 0 ? (
        <Typography sx={{ fontSize: 12.5, color: C.textFaint }}>No requests in this range.</Typography>
      ) : (
        <>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0.75 }}>
            {steps.map((s, i) => (
              <Box key={s.label} sx={{ borderRadius: "12px", background: i === 2 ? C.tintBg : C.gray7, p: 1.25 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: i === 2 ? C.primary : C.ink, lineHeight: 1.1 }}>
                  {formatNumber(s.value)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: C.textSecondary, mt: 0.25 }}>
                  {s.label}
                  {i > 0 && ` · ${pct(s.value, data.people)}%`}
                </Typography>
              </Box>
            ))}
          </Box>
          {lost > 0 && (
            <Typography sx={{ fontSize: 11.5, color: C.warn, mt: 0.75 }}>
              {lost} {lost === 1 ? "person never entered" : "people never entered"} the code
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

function PeopleList({ title, subtitle, rows, render, emptyText }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.ink }}>{title}</Typography>
      <Typography sx={{ fontSize: 11.5, color: C.textSecondary, mb: 1 }}>{subtitle}</Typography>
      {rows.length === 0 ? (
        <Typography sx={{ fontSize: 12.5, color: C.textFaint, py: 1 }}>{emptyText}</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", maxHeight: 220, overflowY: "auto", pr: 0.5 }}>
          {rows.map(render)}
        </Box>
      )}
    </Box>
  );
}

const personRowSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 1.5,
  py: 0.85,
  borderBottom: `1px solid ${C.gray6}`,
  "&:last-of-type": { borderBottom: "none" },
};

// Login & email delivery: sent -> code entered -> completed per flow, plus
// the people who never entered any code (likely spam / not delivered) and
// customers who tried to log in before registering.
export function AuthFunnelCard({ data }) {
  const funnel = data?.funnel || {};
  const stuck = data?.stuck || [];
  const notRegistered = data?.notRegistered || [];
  return (
    <Box sx={{ ...cardSx, p: 3, minWidth: 0 }}>
      <CardTitle
        title="Login & email delivery"
        subtitle="Per person: did the email arrive (code entered) and did they finish? A code that's never entered usually means spam folder or blocked by the provider."
      />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 3, mb: 3 }}>
        {Object.entries(FLOW_LABELS).map(([key, label]) => (
          <FunnelRow key={key} label={label} data={funnel[key] || { emails_sent: 0, people: 0, code_entered: 0, completed: 0 }} />
        ))}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
        <PeopleList
          title="Never entered the code"
          subtitle="Likely didn't receive it — worth contacting"
          rows={stuck}
          emptyText="Everyone entered their code."
          render={(r) => (
            <Box key={`${r.flow}-${r.email}`} sx={personRowSx}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.email}</Typography>
                <Typography sx={{ fontSize: 11.5, color: C.textSecondary }}>
                  {FLOW_LABELS[r.flow] || r.flow} · {r.emails_sent} {r.emails_sent === 1 ? "email" : "emails"} sent
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 11.5, color: C.textFaint, flexShrink: 0 }}>{formatTimeAgo(r.last_sent)}</Typography>
            </Box>
          )}
        />
        <PeopleList
          title="Tried to log in before registering"
          subtitle="Customers with orders but no portal account yet"
          rows={notRegistered}
          emptyText="Nobody in this range."
          render={(r) => (
            <Box key={r.email} sx={personRowSx}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.email}</Typography>
                <Typography sx={{ fontSize: 11.5, color: C.textSecondary }}>
                  {r.tries} {r.tries === 1 ? "attempt" : "attempts"} · {formatTimeAgo(r.last_try)}
                </Typography>
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  px: 1,
                  py: 0.25,
                  borderRadius: 999,
                  flexShrink: 0,
                  color: r.registered_since ? C.ok : C.warn,
                  background: r.registered_since ? C.okBg : C.warnBg,
                }}
              >
                {r.registered_since ? "Registered since" : "Still not registered"}
              </Box>
            </Box>
          )}
        />
      </Box>
    </Box>
  );
}

export function SlowRequestsCard({ rows = [] }) {
  const navigate = useNavigate();
  return (
    <Box sx={{ ...cardSx, p: 3, minWidth: 0 }}>
      <CardTitle title="Slowest requests" subtitle="Single calls that took longest — click for details" />
      {rows.length === 0 ? (
        <Empty>No calls in this range.</Empty>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {rows.map((r) => (
            <Box
              key={r.id}
              onClick={() => navigate(`/usage/requests/${r.id}`)}
              sx={{ ...personRowSx, cursor: "pointer", mx: -1, px: 1, borderRadius: "8px", "&:hover": { background: "#FAFAFA" } }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                <MethodPill method={r.method} />
                <Tooltip title={r.endpoint} placement="top-start">
                  <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <EndpointText endpoint={r.endpoint} />
                  </Box>
                </Tooltip>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                <StatusPill code={r.status_code} />
                <Typography sx={{ fontSize: 12.5, fontFamily: MONO, fontWeight: 700, color: r.duration_ms >= 5000 ? C.error : r.duration_ms >= 2000 ? C.warn : C.ink, minWidth: 64, textAlign: "right" }}>
                  {formatMs(r.duration_ms)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
